/**
 * Marca visual de una empresa cuando todavía no hay logo.
 *
 * Existe UNA sola función porque el preview del onboarding ("así se verá en la
 * app") promete literalmente lo que el usuario va a encontrar en la topbar del
 * panel. Si el preview dijera `AG` y el selector de empresa mostrara otra cosa,
 * la promesa sería falsa — y es el antipatrón explícito de esa pantalla. Ambos
 * consumen `inicialesEmpresa()`.
 */

/**
 * Iniciales de una empresa, siempre en mayúsculas:
 *
 *   "Constructora Andes" → "CA"   (dos o más palabras: inicial de las dos primeras)
 *   "Agentic0"           → "AG"   (una palabra: sus dos primeros caracteres)
 *   ""                   → "—"    (sin dato)
 *
 * Nunca una sola letra: con una sola palabra se toman DOS caracteres, no uno.
 * El único caso que devuelve un carácter es una empresa cuyo nombre entero mide
 * uno ("A"), donde no hay un segundo carácter que tomar.
 *
 * La fuente es el nombre de fantasía y, si está vacío, la razón social: es el
 * mismo orden de preferencia con que se muestra el nombre en la topbar.
 */
export function inicialesEmpresa(
  nombreFantasia?: string | null,
  razonSocial?: string | null,
): string {
  const fuente = (nombreFantasia ?? '').trim() || (razonSocial ?? '').trim()
  if (!fuente) return '—'

  const palabras = fuente.split(/\s+/).filter(Boolean)
  // Se recorre por code point y no por índice de string: `slice(0, 2)` parte al
  // medio un carácter fuera del plano básico y deja media unidad suelta.
  const letras =
    palabras.length >= 2
      ? [...palabras[0]][0] + [...palabras[1]][0]
      : [...palabras[0]].slice(0, 2).join('')

  return letras.toUpperCase()
}
